import { SOURCE_MAX_BYTES } from '../constants';
import { ConversionError } from './errors';

export type SupportedFormat = 'jpeg' | 'png' | 'bmp' | 'gif';

const FORMAT_DETAILS: Record<SupportedFormat, { extensions: string[]; mimeTypes: string[] }> = {
  jpeg: { extensions: ['jpg', 'jpeg'], mimeTypes: ['image/jpeg', 'image/jpg'] },
  png: { extensions: ['png'], mimeTypes: ['image/png'] },
  bmp: { extensions: ['bmp'], mimeTypes: ['image/bmp', 'image/x-ms-bmp'] },
  gif: { extensions: ['gif'], mimeTypes: ['image/gif'] },
};

export function detectFormat(bytes: Uint8Array): SupportedFormat | undefined {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'jpeg';
  }
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return 'png';
  }
  if (bytes.length >= 2 && bytes[0] === 0x42 && bytes[1] === 0x4d) return 'bmp';
  if (bytes.length >= 6) {
    const header = String.fromCharCode(...bytes.subarray(0, 6));
    if (header === 'GIF87a' || header === 'GIF89a') return 'gif';
  }
  return undefined;
}

export async function validateSourceFile(file: File): Promise<SupportedFormat> {
  if (file.size > SOURCE_MAX_BYTES) {
    throw new ConversionError('file-too-large', 'Choose a file no larger than 20 MiB.');
  }
  if (file.size === 0) {
    throw new ConversionError('invalid-file', 'The selected file is empty.');
  }

  const header = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  const format = detectFormat(header);
  if (!format) {
    throw new ConversionError('unsupported-format', 'Choose a JPEG, PNG, BMP, or GIF image.');
  }

  const details = FORMAT_DETAILS[format];
  const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : undefined;
  if (file.type && !details.mimeTypes.includes(file.type.toLowerCase())) {
    throw new ConversionError('invalid-file', 'The file type does not match its contents.');
  }
  if (extension && !details.extensions.includes(extension)) {
    throw new ConversionError('invalid-file', 'The file extension does not match its contents.');
  }
  return format;
}
